/**
 * Shipment Management Screen Desktop
 * 
 * Desktop version of the shipment management interface for managers.
 * Provides comprehensive shipment management with file upload, editing, and distribution.
 * 
 * @module ShipmentManagementScreenDesktop
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Upload,
  Download,
  Send,
  Search,
  Filter,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  X,
  Package,
  Truck,
  Trash2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Info,
  Archive,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Shipment, ShipmentFilters, ShipmentRow, ShipmentGroup, Carrier, ArchivedShipment } from '../../@types/shipment';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { CSVParsingService, SUPPORTED_FILE_EXTENSIONS } from '../../services/CSVParsingService';
import { ShipmentService } from '../../services/ShipmentService';
import { ShipmentNotificationService } from '../../services/ShipmentNotificationService';
import { TrackingLinkService } from '../../services/TrackingLinkService';
import toast, { Toaster } from 'react-hot-toast';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.lg};
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: ${props => {
    switch (props.variant) {
      case 'primary': return ThemeColors.primary.main;
      case 'success': return ThemeColors.success.main;
      case 'danger': return ThemeColors.error.main;
      default: return 'white';
    }
  }};
  color: ${props => props.variant === 'secondary' ? ThemeColors.text.primary : 'white'};
  border: ${props => props.variant === 'secondary' ? `1px solid ${ThemeColors.border.main}` : 'none'};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FileUploadArea = styled.div<{ isDragging?: boolean }>`
  border: 2px dashed ${props => props.isDragging ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: 8px;
  padding: ${Spacing.xl};
  text-align: center;
  background: ${props => props.isDragging ? ThemeColors.primary.light : ThemeColors.background.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: ${Spacing.lg};

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${Shadows.sm};
  margin-bottom: ${Spacing.lg};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${ThemeColors.background.secondary};
`;

const TableHeaderCell = styled.th`
  padding: ${Spacing.md};
  text-align: left;
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  border-bottom: 2px solid ${ThemeColors.border.main};
  position: sticky;
  top: 0;
  background: ${ThemeColors.background.secondary};
  z-index: 10;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ selected?: boolean; edited?: boolean; corrected?: boolean }>`
  background: ${props => 
    props.selected ? ThemeColors.primary.light : 
    props.edited ? ThemeColors.warning.light : 
    props.corrected ? ThemeColors.success.light + '40' : // Light green tint for corrected rows
    'white'};
  border-bottom: 1px solid ${ThemeColors.border.main};
  border-left: ${props => props.corrected ? `3px solid ${ThemeColors.success.main}` : 'none'};
  cursor: pointer;

  &:hover {
    background: ${props => 
      props.corrected ? ThemeColors.success.light + '60' : 
      ThemeColors.background.secondary};
  }
`;

const CorrectionNotice = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.success.light};
  color: ${ThemeColors.success.dark};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  margin-left: ${Spacing.xs};
  cursor: help;
`;

const TableCell = styled.td`
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
`;

const EditableCell = styled.input`
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 4px;
  font-size: ${Typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const SearchBar = styled.div`
  position: relative;
  min-width: 300px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const Select = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const Badge = styled.span<{ variant?: 'success' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background: ${props => {
    switch (props.variant) {
      case 'success': return ThemeColors.success.light;
      case 'warning': return ThemeColors.warning.light;
      default: return ThemeColors.primary.light;
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'success': return ThemeColors.success.dark;
      case 'warning': return ThemeColors.warning.dark;
      default: return ThemeColors.primary.dark;
    }
  }};
`;

const ErrorMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.dark};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacing.xl};
  color: ${ThemeColors.text.secondary};
`;

const TabsContainer = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.lg};
  border-bottom: 2px solid ${ThemeColors.border.main};
`;

const Tab = styled.button<{ active?: boolean }>`
  padding: ${Spacing.md} ${Spacing.lg};
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.active ? ThemeColors.primary.main : 'transparent'};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.secondary};
  font-weight: ${props => props.active ? Typography.fontWeight.semiBold : Typography.fontWeight.regular};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  transition: all 0.2s ease;
  margin-bottom: -2px;

  &:hover {
    color: ${ThemeColors.primary.main};
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ExpandedRow = styled.tr`
  background: ${ThemeColors.background.secondary};
`;

const ExpandedCell = styled.td`
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.main};
`;

const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${Spacing.md};
`;

const DetailsHeader = styled.h4`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const DetailsRow = styled.tr`
  border-bottom: 1px solid ${ThemeColors.border.main};
  
  &:hover {
    background: ${ThemeColors.background.secondary};
  }
`;

const DetailsCell = styled.td`
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
`;

const DetailsLabel = styled(DetailsCell)`
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.secondary};
  width: 200px;
`;

const SectionTitle = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
  margin: ${Spacing.md} 0 ${Spacing.sm} 0;
  padding-bottom: ${Spacing.xs};
  border-bottom: 2px solid ${ThemeColors.primary.main};
`;

const ExpandIcon = styled.div`
  display: inline-flex;
  align-items: center;
  margin-right: ${Spacing.xs};
  cursor: pointer;
`;

const InstructionsPanel = styled.div<{ isExpanded: boolean }>`
  background: ${ThemeColors.background.cards};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: 8px;
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const InstructionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const InstructionsTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const InstructionsContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-top: ${props => props.isExpanded ? Spacing.md : '0'};
`;

const InstructionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  margin-bottom: ${Spacing.md};
`;

const InstructionItem = styled.li`
  padding: ${Spacing.xs} 0;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.xs};

  &::before {
    content: '•';
    color: ${ThemeColors.primary.main};
    font-weight: bold;
    margin-right: ${Spacing.xs};
  }
`;

const RequiredColumns = styled.div`
  margin-top: ${Spacing.md};
`;

const ColumnList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.sm};
`;

const ColumnTag = styled.span<{ required?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.background.secondary};
  color: ${ThemeColors.text.primary};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  border: 1px solid ${ThemeColors.border.light};
`;

const DownloadTemplateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: ${Spacing.sm};
  margin-top: ${Spacing.sm};

  &:hover {
    background: ${ThemeColors.primary.dark};
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TemplateButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.md};
`;

/**
 * Shipment Management Screen Desktop Component
 */
export const ShipmentManagementScreenDesktop: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // State
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [filteredShipments, setFilteredShipments] = useState<Shipment[]>([]);
  const [shipmentGroups, setShipmentGroups] = useState<ShipmentGroup[]>([]);
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedShipments, setSelectedShipments] = useState<Set<string>>(new Set());
  const [editingShipment, setEditingShipment] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<Partial<Shipment>>({});
  const [filters, setFilters] = useState<ShipmentFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [archivedShipments, setArchivedShipments] = useState<ArchivedShipment[]>([]);
  const [archivedShipmentGroups, setArchivedShipmentGroups] = useState<ShipmentGroup[]>([]);

  /**
   * Load shipments on mount and when tab changes
   */
  useEffect(() => {
    if (activeTab === 'active') {
      loadShipments();
    } else {
      loadArchivedShipments();
    }
  }, [activeTab]);

  /**
   * Apply filters when they change
   */
  useEffect(() => {
    applyFilters();
  }, [shipments, filters, searchQuery]);

  /**
   * Load shipments from Firestore
   */
  const loadShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[ShipmentManagementScreen] Loading shipments...');
      
      const data = await ShipmentService.getShipments(filters);
      setShipments(data);
      console.log('[ShipmentManagementScreen] Loaded', data.length, 'shipments');
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error loading shipments:', err);
      setError(err.message || 'Failed to load shipments');
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply filters to shipments
   */
  const applyFilters = () => {
    let filtered = [...shipments];

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.trackingId.toLowerCase().includes(query) ||
        s.shipToName.toLowerCase().includes(query) ||
        s.customerNo.toLowerCase().includes(query) ||
        s.invoiceNo.toLowerCase().includes(query)
      );
    }

    // Apply other filters
    if (filters.salesGroup) {
      filtered = filtered.filter(s => s.salesGroup === filters.salesGroup);
    }

    if (filters.carrier) {
      filtered = filtered.filter(s => s.carrier === filters.carrier);
    }

    if (filters.status) {
      filtered = filtered.filter(s => s.status === filters.status);
    }

    setFilteredShipments(filtered);
    
    // Group shipments by tracking ID
    const groups = ShipmentService.groupShipmentsByTrackingId(filtered);
    setShipmentGroups(groups);
  };

  /**
   * Handle file upload
   */
  const handleFileUpload = async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[ShipmentManagementScreen] Parsing file:', file.name);

      // Parse CSV or Excel file
      const parseResult = await CSVParsingService.parseShipmentFile(file);
      
      if (parseResult.errors.length > 0) {
        console.warn('[ShipmentManagementScreen] Parse errors:', parseResult.errors);
        toast.error(`File parsed with ${parseResult.errors.length} errors. Check console for details.`);
      }

      if (parseResult.shipments.length === 0) {
        throw new Error('No valid shipments found in file');
      }

      // Check for Purolator tracking numbers that need review/correction
      const purolatorCorrections: Array<{ row: number; original: string; suggested: string; needsReview: boolean }> = [];
      parseResult.shipments.forEach((shipment, index) => {
        const shipZone = (shipment.ShipZone || '').toUpperCase();
        if (shipZone.includes('PUROL') || shipZone.includes('PUROLATOR')) {
          const check = TrackingLinkService.checkPurolatorTrackingNumber(
            shipment.TrackingID || '',
            shipment.ShipZone || ''
          );
          
          // Check if it's a very long string that might need manual review
          const isVeryLong = (shipment.TrackingID || '').trim().length >= 30;
          const hasValidPattern = /^(609\d{9}|610\d{9}|904\d+|905\d+)$/.test(check.suggested);
          const needsReview = isVeryLong && !hasValidPattern && !check.needsCorrection;
          
          if (check.needsCorrection || needsReview) {
            purolatorCorrections.push({
              row: index + 2, // +2 because index is 0-based and we skip header row
              original: check.original,
              suggested: check.suggested,
              needsReview
            });
          }
        }
      });

      // Show suggestion alert if Purolator tracking numbers were corrected or need review
      if (purolatorCorrections.length > 0) {
        const corrections = purolatorCorrections.filter(c => !c.needsReview);
        const reviews = purolatorCorrections.filter(c => c.needsReview);
        
        let message = '';
        if (corrections.length > 0) {
          const examples = corrections.slice(0, 3).map(c => 
            `Row ${c.row}: "${c.original}" → "${c.suggested}"`
          ).join('\n');
          const moreText = corrections.length > 3 ? `\n... and ${corrections.length - 3} more` : '';
          message += `✓ Corrected ${corrections.length} Purolator tracking number(s).\n\nExamples:\n${examples}${moreText}\n\n`;
        }
        
        if (reviews.length > 0) {
          const reviewExamples = reviews.slice(0, 2).map(c => 
            `Row ${c.row}: "${c.original}" (may need manual review)`
          ).join('\n');
          message += `⚠ ${reviews.length} Purolator tracking number(s) may need manual review:\n${reviewExamples}\n\nPlease verify these tracking numbers are correct.`;
        }
        
        message += '\n\nThe corrected tracking numbers have been automatically extracted and saved.';
        
        toast.success(message, { duration: 10000 });
        console.log('[ShipmentManagementScreen] Purolator tracking number corrections:', purolatorCorrections);
      }

      // Create upload record
      const uploadId = await ShipmentService.createUpload({
        uploadedBy: user!.id,
        uploadedAt: new Date() as any,
        fileName: file.name,
        fileSize: file.size,
        totalRows: parseResult.metadata.totalRows,
        processedRows: parseResult.metadata.validRows,
        failedRows: parseResult.metadata.invalidRows,
        status: 'processing',
        errors: parseResult.errors
      });

      // Create shipments in Firestore
      await ShipmentService.createShipments(parseResult.shipments, uploadId, user!.id);

      // Update upload status
      await ShipmentService.updateUpload(uploadId, { status: 'completed' });

      toast.success(`Successfully imported ${parseResult.shipments.length} shipments`);
      console.log('[ShipmentManagementScreen] Successfully imported shipments');

      // Reload shipments
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error uploading file:', err);
      setError(err.message || 'Failed to upload file');
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle file drop
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && CSVParsingService.isSupportedFile(file)) {
      handleFileUpload(file);
    } else {
      toast.error(`Please upload a supported file format: ${SUPPORTED_FILE_EXTENSIONS.join(', ')}`);
    }
  };

  /**
   * Handle file select
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  /**
   * Handle shipment edit
   */
  const handleEditShipment = (shipmentId: string, field: keyof Shipment, value: any) => {
    setEditingShipment(shipmentId);
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Save edited shipment
   */
  const handleSaveShipment = async (shipmentId: string) => {
    try {
      if (!user) return;

      await ShipmentService.updateShipment(shipmentId, editedData, user.id);
      
      // Update local state
      setShipments(prev => prev.map(s => 
        s.id === shipmentId ? { ...s, ...editedData } : s
      ));

      setEditingShipment(null);
      setEditedData({});
      toast.success('Shipment updated successfully');
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error saving shipment:', err);
      toast.error('Failed to save shipment');
    }
  };

  /**
   * Handle track shipment
   */
  const handleTrackShipment = (shipment: Shipment) => {
    // Always regenerate tracking URL to ensure it's up-to-date (especially for Purolator freight IDs)
    // This ensures we use the latest logic even if the stored URL is outdated
    if (shipment.trackingId && shipment.carrier) {
      const url = TrackingLinkService.generateTrackingUrl(shipment.carrier, shipment.trackingId);
      if (url) {
        window.open(url, '_blank');
        return;
      }
    }
    
    // Fallback to stored URL if generation fails
    if (shipment.trackingUrl) {
      window.open(shipment.trackingUrl, '_blank');
      return;
    }
    
    toast.error('No tracking URL available for this shipment');
  };

  /**
   * Handle send to sales reps
   */
  const handleSendToSalesReps = async () => {
    if (selectedShipments.size === 0) {
      toast.error('Please select shipments to send');
      return;
    }

    try {
      setLoading(true);
      const selected = filteredShipments.filter(s => selectedShipments.has(s.id));
      
      // Group by sales group
      const bySalesGroup = new Map<string, Shipment[]>();
      for (const shipment of selected) {
        const group = shipment.salesGroup || 'UNKNOWN';
        if (!bySalesGroup.has(group)) {
          bySalesGroup.set(group, []);
        }
        bySalesGroup.get(group)!.push(shipment);
      }

      // Send to each sales group
      for (const [salesGroup, groupShipments] of bySalesGroup.entries()) {
        await ShipmentNotificationService.sendShipmentsToSalesReps(
          groupShipments,
          salesGroup,
          user!.id
        );
      }

      toast.success(`Sent ${selected.length} shipments to sales reps`);
      setSelectedShipments(new Set());
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error sending to sales reps:', err);
      toast.error(err.message || 'Failed to send shipments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get all shipment IDs in a group
   */
  const getGroupShipmentIds = (group: ShipmentGroup): string[] => {
    return group.shipments.map(s => s.id);
  };

  /**
   * Check if all shipments in a group are selected
   */
  const isGroupSelected = (group: ShipmentGroup): boolean => {
    const groupIds = getGroupShipmentIds(group);
    return groupIds.length > 0 && groupIds.every(id => selectedShipments.has(id));
  };

  /**
   * Toggle selection for all shipments in a group
   */
  const toggleGroupSelection = (group: ShipmentGroup) => {
    const groupIds = getGroupShipmentIds(group);
    const newSelected = new Set(selectedShipments);
    const allSelected = isGroupSelected(group);
    
    if (allSelected) {
      // Deselect all
      groupIds.forEach(id => newSelected.delete(id));
    } else {
      // Select all
      groupIds.forEach(id => newSelected.add(id));
    }
    
    setSelectedShipments(newSelected);
  };

  /**
   * Toggle expansion of a group
   */
  const toggleGroupExpansion = (trackingId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(trackingId)) {
      newExpanded.delete(trackingId);
    } else {
      newExpanded.add(trackingId);
    }
    setExpandedGroups(newExpanded);
  };

  /**
   * Check if a group is expanded
   */
  const isGroupExpanded = (trackingId: string): boolean => {
    return expandedGroups.has(trackingId);
  };

  /**
   * Handle delete selected shipments (only unassigned ones)
   */
  const handleDeleteSelected = async () => {
    if (selectedShipments.size === 0) {
      toast.error('Please select shipments to delete');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedShipments.size} shipment(s)? This action cannot be undone. Alternatively, you can cancel and archive them for later use.`)) {
      return;
    }

    try {
      setLoading(true);
      const shipmentIds = Array.from(selectedShipments);
      await ShipmentService.deleteUnassignedShipments(shipmentIds);
      
      toast.success(`Deleted unassigned shipments`);
      setSelectedShipments(new Set());
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error deleting shipments:', err);
      toast.error(err.message || 'Failed to delete shipments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle clear all shipments (only unassigned ones)
   */
  const handleClearAll = async () => {
    if (!confirm('Are you sure you want to delete ALL unassigned shipments? This action cannot be undone. Alternatively, you can archive them for later use.')) {
      return;
    }

    try {
      setLoading(true);
      await ShipmentService.deleteAllUnassignedShipments();
      
      toast.success('All unassigned shipments deleted');
      setSelectedShipments(new Set());
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error clearing shipments:', err);
      toast.error(err.message || 'Failed to clear shipments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle archive selected shipments
   */
  const handleArchiveSelected = async () => {
    if (selectedShipments.size === 0) {
      toast.error('Please select shipments to archive');
      return;
    }

    if (!confirm(`Are you sure you want to archive ${selectedShipments.size} shipment(s)? Only unassigned shipments will be archived.`)) {
      return;
    }

    try {
      setLoading(true);
      const shipmentIds = Array.from(selectedShipments);
      await ShipmentService.archiveShipments(shipmentIds, user!.id);
      
      toast.success(`Archived shipments`);
      setSelectedShipments(new Set());
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error archiving shipments:', err);
      toast.error(err.message || 'Failed to archive shipments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle archive all shipments
   */
  const handleArchiveAll = async () => {
    if (!confirm('Are you sure you want to archive ALL unassigned shipments? Assigned shipments will be preserved.')) {
      return;
    }

    try {
      setLoading(true);
      const allShipmentIds = shipments.map(s => s.id);
      await ShipmentService.archiveShipments(allShipmentIds, user!.id);
      
      toast.success('All unassigned shipments archived');
      setSelectedShipments(new Set());
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error archiving shipments:', err);
      toast.error(err.message || 'Failed to archive shipments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load archived shipments
   */
  const loadArchivedShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[ShipmentManagementScreen] Loading archived shipments...');
      
      const data = await ShipmentService.getArchivedShipments(filters);
      setArchivedShipments(data);
      
      // Group archived shipments by tracking ID
      const groups = ShipmentService.groupShipmentsByTrackingId(data);
      setArchivedShipmentGroups(groups);
      
      console.log('[ShipmentManagementScreen] Loaded', data.length, 'archived shipments in', groups.length, 'groups');
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error loading archived shipments:', err);
      setError(err.message || 'Failed to load archived shipments');
      toast.error('Failed to load archived shipments');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle restore archived shipment
   */
  const handleRestoreArchivedShipment = async (archivedShipmentId: string) => {
    if (!confirm('Are you sure you want to restore this shipment? It will be moved back to active shipments.')) {
      return;
    }

    try {
      setLoading(true);
      await ShipmentService.restoreArchivedShipment(archivedShipmentId, user!.id);
      
      toast.success('Shipment restored');
      await loadArchivedShipments();
      // Switch to active tab to show restored shipment
      setActiveTab('active');
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreen] Error restoring shipment:', err);
      toast.error(err.message || 'Failed to restore shipment');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format address for display
   */
  const formatAddress = (shipment: Shipment): string => {
    const parts = [
      shipment.shipToAddress1,
      shipment.shipToCity,
      shipment.shipToState
    ].filter(Boolean);
    return parts.join(', ') || 'N/A';
  };

  /**
   * Get unique sales groups
   */
  const salesGroups = useMemo(() => {
    const groups = new Set(shipments.map(s => s.salesGroup).filter(Boolean));
    return Array.from(groups).sort();
  }, [shipments]);

  /**
   * Get unique carriers
   */
  const carriers = useMemo(() => {
    const carrierSet = new Set(shipments.map(s => s.carrier));
    return Array.from(carrierSet).sort();
  }, [shipments]);

  return (
    <PageContainer isMenuCollapsed={false}>
      <Toaster position="top-right" />
      <MainContent>
        <Header>
          <Title>Shipment Management</Title>
        </Header>

        {error && (
          <ErrorMessage>
            <AlertCircle size={20} style={{ marginRight: Spacing.sm, verticalAlign: 'middle' }} />
            {error}
          </ErrorMessage>
        )}

        {/* Tabs */}
        <TabsContainer>
          <Tab active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
            <Package size={18} />
            Active Shipments ({shipmentGroups.length})
          </Tab>
          <Tab active={activeTab === 'archived'} onClick={() => setActiveTab('archived')}>
            <Archive size={18} />
            Archived Shipments ({archivedShipmentGroups.length})
          </Tab>
        </TabsContainer>

        {/* Instructions Panel */}
        <InstructionsPanel isExpanded={instructionsExpanded}>
          <InstructionsHeader onClick={() => setInstructionsExpanded(!instructionsExpanded)}>
            <InstructionsTitle>
              <Info size={20} />
              File Upload Instructions
            </InstructionsTitle>
            {instructionsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </InstructionsHeader>
          <InstructionsContent isExpanded={instructionsExpanded}>
            <InstructionsList>
              <InstructionItem>
                Your shipment file must be in CSV or Excel format (.csv, .xlsx, .xls)
              </InstructionItem>
              <InstructionItem>
                The first row must contain the column headers exactly as shown below
              </InstructionItem>
              <InstructionItem>
                Dates must be in 4-digit year format (MM/DD/YYYY, e.g., 12/15/2025)
              </InstructionItem>
              <InstructionItem>
                Required fields must have values for all rows
              </InstructionItem>
              <InstructionItem>
                Download the template files below to see the correct format with example data
              </InstructionItem>
            </InstructionsList>

            <RequiredColumns>
              <strong style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.primary }}>
                Required Columns (must be present):
              </strong>
              <ColumnList>
                <ColumnTag required>TrackingID</ColumnTag>
                <ColumnTag required>ShipZone</ColumnTag>
                <ColumnTag required>CustomerNo</ColumnTag>
                <ColumnTag required>InvoiceNo</ColumnTag>
                <ColumnTag required>SalesOrderNo</ColumnTag>
                <ColumnTag required>ShipToName</ColumnTag>
                <ColumnTag required>ShipToAddress1</ColumnTag>
                <ColumnTag required>ShipToCity</ColumnTag>
                <ColumnTag required>ShipToState</ColumnTag>
                <ColumnTag required>ShipDate</ColumnTag>
                <ColumnTag required>ShipVia</ColumnTag>
                <ColumnTag required>WarehouseCode</ColumnTag>
                <ColumnTag required>QuantityShipped</ColumnTag>
              </ColumnList>
            </RequiredColumns>

            <RequiredColumns style={{ marginTop: Spacing.lg }}>
              <strong style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.primary }}>
                Optional Columns (may be included):
              </strong>
              <ColumnList>
                <ColumnTag>SalespersonNo</ColumnTag>
                <ColumnTag>SALES_GROUP</ColumnTag>
                <ColumnTag>SortField</ColumnTag>
                <ColumnTag>OrderDate</ColumnTag>
                <ColumnTag>CustomerPONo</ColumnTag>
                <ColumnTag>PromiseDate</ColumnTag>
                <ColumnTag>STORE</ColumnTag>
                <ColumnTag>TelephoneNo</ColumnTag>
                <ColumnTag>Pallets</ColumnTag>
                <ColumnTag>Pieces</ColumnTag>
                <ColumnTag>ProductLine</ColumnTag>
                <ColumnTag>Category2</ColumnTag>
                <ColumnTag>DetailSeqNo</ColumnTag>
                <ColumnTag>ItemCode</ColumnTag>
                <ColumnTag>ItemCodeDesc</ColumnTag>
                <ColumnTag>QuantityOrdered</ColumnTag>
                <ColumnTag>QuantityBackordered</ColumnTag>
              </ColumnList>
            </RequiredColumns>

            <TemplateButtons>
              <DownloadTemplateButton
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/assets/shipment-template.csv';
                  link.download = 'shipment-template.csv';
                  link.click();
                }}
              >
                <Download size={16} />
                Download CSV Template
              </DownloadTemplateButton>
              <DownloadTemplateButton
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/assets/shipment-template.xlsx';
                  link.download = 'shipment-template.xlsx';
                  link.click();
                }}
              >
                <Download size={16} />
                Download Excel Template
              </DownloadTemplateButton>
            </TemplateButtons>
          </InstructionsContent>
        </InstructionsPanel>

        <FileUploadArea
          isDragging={isDragging}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <Upload size={48} style={{ marginBottom: Spacing.md, color: ThemeColors.primary.main }} />
          <p style={{ fontSize: Typography.fontSize.lg, marginBottom: Spacing.xs }}>
            Drop file here or click to upload
          </p>
          <p style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary }}>
            Supported formats: CSV, Excel (.xlsx, .xls)
          </p>
          <p style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary }}>
            Supported format: CSV (comma or semicolon-delimited)
          </p>
          <FileInput
            id="file-input"
            type="file"
            accept=".csv,.xlsx,.xls,.xlsm,.xlsb"
            onChange={handleFileSelect}
          />
        </FileUploadArea>

        <Toolbar>
          <ToolbarSection>
            <SearchBar>
              <Search size={18} />
              <SearchInput
                placeholder="Search shipments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
            <Select
              value={filters.salesGroup || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, salesGroup: e.target.value || undefined }))}
            >
              <option value="">All Sales Groups</option>
              {salesGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </Select>
            <Select
              value={filters.carrier || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, carrier: e.target.value as Carrier || undefined }))}
            >
              <option value="">All Carriers</option>
              {carriers.map(carrier => (
                <option key={carrier} value={carrier}>
                  {TrackingLinkService.getCarrierDisplayName(carrier)}
                </option>
              ))}
            </Select>
          </ToolbarSection>
          <ToolbarSection>
            <Button variant="secondary" onClick={loadShipments} disabled={loading}>
              <RefreshCw size={16} />
              Refresh
            </Button>
            {activeTab === 'active' && (
              <>
                {selectedShipments.size > 0 && (
                  <>
                    <Button variant="success" onClick={handleSendToSalesReps} disabled={loading}>
                      <Send size={16} />
                      Send to Sales Reps ({selectedShipments.size})
                    </Button>
                    <Button variant="secondary" onClick={handleArchiveSelected} disabled={loading}>
                      <Archive size={16} />
                      Archive Selected ({selectedShipments.size})
                    </Button>
                    <Button variant="danger" onClick={handleDeleteSelected} disabled={loading}>
                      <Trash2 size={16} />
                      Delete Selected ({selectedShipments.size})
                    </Button>
                  </>
                )}
                {shipments.length > 0 && (
                  <>
                    <Button variant="secondary" onClick={handleArchiveAll} disabled={loading}>
                      <Archive size={16} />
                      Archive All
                    </Button>
                    <Button variant="danger" onClick={handleClearAll} disabled={loading}>
                      <Trash2 size={16} />
                      Clear All
                    </Button>
                  </>
                )}
              </>
            )}
          </ToolbarSection>
        </Toolbar>

        {loading && (activeTab === 'active' ? shipmentGroups.length === 0 : archivedShipmentGroups.length === 0) ? (
          <LoadingSpinner>
            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
            Loading {activeTab === 'archived' ? 'archived ' : ''}shipments...
          </LoadingSpinner>
        ) : activeTab === 'archived' ? (
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Tracking ID</TableHeaderCell>
                  <TableHeaderCell>Carrier</TableHeaderCell>
                  <TableHeaderCell>Customer No</TableHeaderCell>
                  <TableHeaderCell>Customer Name</TableHeaderCell>
                  <TableHeaderCell>Address</TableHeaderCell>
                  <TableHeaderCell>Sales Group</TableHeaderCell>
                  <TableHeaderCell>Ship Date</TableHeaderCell>
                  <TableHeaderCell>Archived At</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {archivedShipments.map((shipment) => (
                  <TableRow key={shipment.id}>
                    <TableCell>{shipment.trackingId}</TableCell>
                    <TableCell>
                      <Badge variant="info">
                        {TrackingLinkService.getCarrierDisplayName(shipment.carrier)}
                      </Badge>
                    </TableCell>
                    <TableCell>{shipment.customerNo}</TableCell>
                    <TableCell>{shipment.shipToName}</TableCell>
                    <TableCell>{formatAddress(shipment)}</TableCell>
                    <TableCell>{shipment.salesGroup}</TableCell>
                    <TableCell>{shipment.shipDate}</TableCell>
                    <TableCell>
                      {shipment.archivedAt 
                        ? (shipment.archivedAt.toDate ? shipment.archivedAt.toDate().toLocaleDateString() : new Date(shipment.archivedAt.seconds * 1000).toLocaleDateString())
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="success"
                        onClick={() => handleRestoreArchivedShipment(shipment.id)}
                        disabled={loading}
                        style={{ padding: Spacing.xs, fontSize: Typography.fontSize.xs }}
                      >
                        <RotateCcw size={14} />
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell style={{ width: '40px' }}>
                    <Checkbox
                      checked={shipmentGroups.length > 0 && shipmentGroups.every(g => isGroupSelected(g))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const allIds = shipmentGroups.flatMap(g => getGroupShipmentIds(g));
                          setSelectedShipments(new Set(allIds));
                        } else {
                          setSelectedShipments(new Set());
                        }
                      }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell>Tracking ID</TableHeaderCell>
                  <TableHeaderCell>Carrier</TableHeaderCell>
                  <TableHeaderCell>Customer No</TableHeaderCell>
                  <TableHeaderCell>Customer Name</TableHeaderCell>
                  <TableHeaderCell>Address</TableHeaderCell>
                  <TableHeaderCell>Sales Group</TableHeaderCell>
                  <TableHeaderCell>Total Items</TableHeaderCell>
                  <TableHeaderCell>Qty Ordered</TableHeaderCell>
                  <TableHeaderCell>Qty Shipped</TableHeaderCell>
                  <TableHeaderCell>Ship Date</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {shipmentGroups.map((group) => {
                  const firstShipment = group.shipments[0];
                  const totalQuantityOrdered = group.shipments.reduce((sum, s) => sum + s.quantityOrdered, 0);
                  const isSelected = isGroupSelected(group);
                  const isExpanded = isGroupExpanded(group.trackingId);
                  
                  return (
                    <React.Fragment key={group.trackingId}>
                  <TableRow
                        selected={isSelected}
                        edited={editingShipment === firstShipment.id}
                        corrected={!!group.originalTrackingId}
                        onClick={(e) => {
                          // Don't expand if clicking on checkbox, button, or editable cell
                          const target = e.target as HTMLElement;
                          if (
                            target.closest('input[type="checkbox"]') ||
                            target.closest('button') ||
                            target.closest('input[type="text"]') ||
                            target.closest('input[type="number"]')
                          ) {
                            return;
                          }
                          toggleGroupExpansion(group.trackingId);
                        }}
                        style={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                            <ExpandIcon onClick={(e) => { e.stopPropagation(); toggleGroupExpansion(group.trackingId); }}>
                              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </ExpandIcon>
                      <Checkbox
                              checked={isSelected}
                        onChange={(e) => {
                                e.stopPropagation();
                                toggleGroupSelection(group);
                              }}
                            />
                          </div>
                    </TableCell>
                    <TableCell>
                        {editingShipment === firstShipment.id ? (
                        <EditableCell
                            value={editedData.trackingId ?? group.trackingId}
                            onChange={(e) => handleEditShipment(firstShipment.id, 'trackingId', e.target.value)}
                            onBlur={() => handleSaveShipment(firstShipment.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveShipment(firstShipment.id);
                            }
                          }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: Spacing.xs }}>
                          <span
                            onClick={() => {
                                setEditingShipment(firstShipment.id);
                                setEditedData({ trackingId: group.trackingId });
                            }}
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          >
                              {group.trackingId}
                          </span>
                          {group.originalTrackingId && (
                            <CorrectionNotice
                              title={`Tracking number was corrected from: ${group.originalTrackingId}`}
                            >
                              <CheckCircle size={12} />
                              <span>Corrected</span>
                            </CorrectionNotice>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                        {editingShipment === firstShipment.id ? (
                        <EditableCell
                            value={editedData.shipZone ?? firstShipment.shipZone}
                            onChange={(e) => handleEditShipment(firstShipment.id, 'shipZone', e.target.value)}
                            onBlur={() => handleSaveShipment(firstShipment.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveShipment(firstShipment.id);
                            }
                          }}
                        />
                      ) : (
                        <Badge variant="info">
                            {TrackingLinkService.getCarrierDisplayName(group.carrier)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                        {editingShipment === firstShipment.id ? (
                        <EditableCell
                            value={editedData.customerNo ?? firstShipment.customerNo}
                            onChange={(e) => handleEditShipment(firstShipment.id, 'customerNo', e.target.value)}
                            onBlur={() => handleSaveShipment(firstShipment.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveShipment(firstShipment.id);
                            }
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => {
                              setEditingShipment(firstShipment.id);
                              setEditedData({ customerNo: firstShipment.customerNo });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                            {firstShipment.customerNo}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                        {editingShipment === firstShipment.id ? (
                        <EditableCell
                            value={editedData.shipToName ?? group.shipToName}
                            onChange={(e) => handleEditShipment(firstShipment.id, 'shipToName', e.target.value)}
                            onBlur={() => handleSaveShipment(firstShipment.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveShipment(firstShipment.id);
                            }
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => {
                              setEditingShipment(firstShipment.id);
                              setEditedData({ shipToName: group.shipToName });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                            {group.shipToName}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                        {editingShipment === firstShipment.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing.xs }}>
                          <EditableCell
                            placeholder="Address"
                              value={editedData.shipToAddress1 ?? group.shipToAddress}
                              onChange={(e) => handleEditShipment(firstShipment.id, 'shipToAddress1', e.target.value)}
                              onBlur={() => handleSaveShipment(firstShipment.id)}
                          />
                          <EditableCell
                            placeholder="City"
                              value={editedData.shipToCity ?? group.shipToCity}
                              onChange={(e) => handleEditShipment(firstShipment.id, 'shipToCity', e.target.value)}
                              onBlur={() => handleSaveShipment(firstShipment.id)}
                          />
                          <EditableCell
                            placeholder="Province"
                              value={editedData.shipToState ?? group.shipToState}
                              onChange={(e) => handleEditShipment(firstShipment.id, 'shipToState', e.target.value)}
                              onBlur={() => handleSaveShipment(firstShipment.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                  handleSaveShipment(firstShipment.id);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <span
                          onClick={() => {
                              setEditingShipment(firstShipment.id);
                            setEditedData({
                                shipToAddress1: group.shipToAddress,
                                shipToCity: group.shipToCity,
                                shipToState: group.shipToState
                            });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                            {group.shipToAddress}, {group.shipToCity}, {group.shipToState}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                        {editingShipment === firstShipment.id ? (
                        <EditableCell
                            value={editedData.salesGroup ?? firstShipment.salesGroup}
                            onChange={(e) => handleEditShipment(firstShipment.id, 'salesGroup', e.target.value)}
                            onBlur={() => handleSaveShipment(firstShipment.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveShipment(firstShipment.id);
                            }
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => {
                              setEditingShipment(firstShipment.id);
                              setEditedData({ salesGroup: firstShipment.salesGroup });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                            {firstShipment.salesGroup}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                        <Badge variant="info">{group.totalItems}</Badge>
                    </TableCell>
                    <TableCell>
                        <strong>{totalQuantityOrdered}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{group.totalQuantity}</strong>
                      </TableCell>
                      <TableCell>
                        {editingShipment === firstShipment.id ? (
                        <EditableCell
                            value={editedData.shipDate ?? group.shipDate}
                            onChange={(e) => handleEditShipment(firstShipment.id, 'shipDate', e.target.value)}
                            onBlur={() => handleSaveShipment(firstShipment.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSaveShipment(firstShipment.id);
                            }
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => {
                              setEditingShipment(firstShipment.id);
                              setEditedData({ shipDate: group.shipDate });
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                            {group.shipDate}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                        <Badge variant={firstShipment.status === 'sent' ? 'success' : 'warning'}>
                          {firstShipment.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: Spacing.xs }}>
                        <Button
                          variant="secondary"
                            onClick={() => handleTrackShipment(firstShipment)}
                            disabled={!group.trackingId}
                          style={{ padding: Spacing.xs }}
                          title={group.trackingUrl ? 'Track shipment' : 'Open tracking page'}
                        >
                          <ExternalLink size={14} />
                          Track
                        </Button>
                        <Button
                          variant="danger"
                          onClick={async () => {
                              if (confirm(`Delete all ${group.totalItems} shipment(s) with tracking ID ${group.trackingId}?`)) {
                              try {
                                  const shipmentIds = group.shipments.map(s => s.id);
                                  await ShipmentService.deleteShipments(shipmentIds);
                                  toast.success(`Deleted ${group.totalItems} shipment(s)`);
                                await loadShipments();
                              } catch (err: any) {
                                  toast.error('Failed to delete shipments');
                              }
                            }
                          }}
                          style={{ padding: Spacing.xs }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                    {isExpanded && (
                      <ExpandedRow>
                        <ExpandedCell colSpan={12}>
                          <DetailsHeader>
                            Shipment Details - {group.trackingId}
                            {group.originalTrackingId && (
                              <CorrectionNotice
                                style={{ marginLeft: Spacing.md }}
                                title={`Original tracking number: ${group.originalTrackingId}`}
                              >
                                <CheckCircle size={14} />
                                <span>Tracking number corrected from: {group.originalTrackingId}</span>
                              </CorrectionNotice>
                            )}
                          </DetailsHeader>
                          
                          {/* General Information */}
                          <SectionTitle>General Information</SectionTitle>
                          <DetailsTable>
                            <tbody>
                              <DetailsRow>
                                <DetailsLabel>Tracking ID:</DetailsLabel>
                                <DetailsCell>
                                  {group.trackingId}
                                  {group.originalTrackingId && (
                                    <span style={{ 
                                      marginLeft: Spacing.sm, 
                                      fontSize: Typography.fontSize.xs, 
                                      color: ThemeColors.text.secondary 
                                    }}>
                                      (corrected from {group.originalTrackingId})
                                    </span>
                                  )}
                                </DetailsCell>
                                <DetailsLabel>Sales Group:</DetailsLabel>
                                <DetailsCell>{firstShipment.salesGroup}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Salesperson No:</DetailsLabel>
                                <DetailsCell>{firstShipment.salespersonNo || 'N/A'}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Invoice No:</DetailsLabel>
                                <DetailsCell>{firstShipment.invoiceNo}</DetailsCell>
                                <DetailsLabel>Sales Order No:</DetailsLabel>
                                <DetailsCell>{firstShipment.salesOrderNo}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Order Date:</DetailsLabel>
                                <DetailsCell>{firstShipment.orderDate}</DetailsCell>
                                <DetailsLabel>Customer PO No:</DetailsLabel>
                                <DetailsCell>{firstShipment.customerPONo || 'N/A'}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Promise Date:</DetailsLabel>
                                <DetailsCell>{firstShipment.promiseDate || 'N/A'}</DetailsCell>
                                <DetailsLabel>Ship Date:</DetailsLabel>
                                <DetailsCell>{firstShipment.shipDate}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Ship Via:</DetailsLabel>
                                <DetailsCell>{firstShipment.shipVia}</DetailsCell>
                                <DetailsLabel>Ship Zone:</DetailsLabel>
                                <DetailsCell>{firstShipment.shipZone}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Warehouse Code:</DetailsLabel>
                                <DetailsCell>{firstShipment.warehouseCode}</DetailsCell>
                                <DetailsLabel>Pallets:</DetailsLabel>
                                <DetailsCell>{firstShipment.pallets ?? 'N/A'}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Pieces:</DetailsLabel>
                                <DetailsCell>{firstShipment.pieces ?? 'N/A'}</DetailsCell>
                                <DetailsLabel>Status:</DetailsLabel>
                                <DetailsCell>
                                  <Badge variant={firstShipment.status === 'sent' ? 'success' : 'warning'}>
                                    {firstShipment.status}
                                  </Badge>
                                </DetailsCell>
                              </DetailsRow>
                            </tbody>
                          </DetailsTable>

                          {/* Customer Information */}
                          <SectionTitle>Customer Information</SectionTitle>
                          <DetailsTable>
                            <tbody>
                              <DetailsRow>
                                <DetailsLabel>Customer No:</DetailsLabel>
                                <DetailsCell>{firstShipment.customerNo}</DetailsCell>
                                <DetailsLabel>Customer Name:</DetailsLabel>
                                <DetailsCell>{firstShipment.shipToName}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Store:</DetailsLabel>
                                <DetailsCell>{firstShipment.store || 'N/A'}</DetailsCell>
                                <DetailsLabel>Telephone:</DetailsLabel>
                                <DetailsCell>{firstShipment.telephoneNo || 'N/A'}</DetailsCell>
                              </DetailsRow>
                              <DetailsRow>
                                <DetailsLabel>Address:</DetailsLabel>
                                <DetailsCell colSpan={3}>
                                  {firstShipment.shipToAddress1}, {firstShipment.shipToCity}, {firstShipment.shipToState}
                                </DetailsCell>
                              </DetailsRow>
                            </tbody>
                          </DetailsTable>

                          {/* Items in Shipment */}
                          <SectionTitle>Items in Shipment ({group.totalItems} items)</SectionTitle>
                          <DetailsTable>
                            <thead>
                              <tr style={{ background: ThemeColors.background.secondary }}>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold }}>Seq</DetailsCell>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold }}>Item Code</DetailsCell>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold }}>Description</DetailsCell>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold }}>Product Line</DetailsCell>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold }}>Category</DetailsCell>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold, textAlign: 'right' }}>Qty Ordered</DetailsCell>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold, textAlign: 'right' }}>Qty Shipped</DetailsCell>
                                <DetailsCell as="th" style={{ fontWeight: Typography.fontWeight.semiBold, textAlign: 'right' }}>Qty Backordered</DetailsCell>
                              </tr>
                            </thead>
                            <tbody>
                              {group.shipments
                                .sort((a, b) => {
                                  const seqA = Number(a.detailSeqNo || 0);
                                  const seqB = Number(b.detailSeqNo || 0);
                                  return seqA - seqB;
                                })
                                .map((shipment, idx) => {
                                  // Use product/supply name from database (fetched by ItemCode), fallback to CSV description only if not found
                                  const displayName = shipment.itemNameEN || shipment.itemCodeDesc || 'N/A';
                                  return (
                                    <DetailsRow key={shipment.id}>
                                      <DetailsCell>{shipment.detailSeqNo || idx + 1}</DetailsCell>
                                      <DetailsCell>{shipment.itemCode || 'N/A'}</DetailsCell>
                                      <DetailsCell>{displayName}</DetailsCell>
                                      <DetailsCell>{shipment.productLine || 'N/A'}</DetailsCell>
                                      <DetailsCell>{shipment.category2 || 'N/A'}</DetailsCell>
                                      <DetailsCell style={{ textAlign: 'right' }}>{shipment.quantityOrdered}</DetailsCell>
                                      <DetailsCell style={{ textAlign: 'right' }}>{shipment.quantityShipped}</DetailsCell>
                                      <DetailsCell style={{ textAlign: 'right' }}>{shipment.quantityBackordered}</DetailsCell>
                                    </DetailsRow>
                                  );
                                })}
                              <DetailsRow style={{ background: ThemeColors.background.secondary, fontWeight: Typography.fontWeight.semiBold }}>
                                <DetailsCell colSpan={5} style={{ textAlign: 'right', fontWeight: Typography.fontWeight.semiBold }}>
                                  Totals:
                                </DetailsCell>
                                <DetailsCell style={{ textAlign: 'right', fontWeight: Typography.fontWeight.semiBold }}>
                                  {totalQuantityOrdered}
                                </DetailsCell>
                                <DetailsCell style={{ textAlign: 'right', fontWeight: Typography.fontWeight.semiBold }}>
                                  {group.totalQuantity}
                                </DetailsCell>
                                <DetailsCell style={{ textAlign: 'right', fontWeight: Typography.fontWeight.semiBold }}>
                                  {group.shipments.reduce((sum, s) => sum + s.quantityBackordered, 0)}
                                </DetailsCell>
                              </DetailsRow>
                            </tbody>
                          </DetailsTable>
                        </ExpandedCell>
                      </ExpandedRow>
                    )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {shipmentGroups.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: Spacing.xl, color: ThemeColors.text.secondary }}>
            <Package size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
            <p>No shipments found. Upload a CSV or Excel file to get started.</p>
          </div>
        )}
      </MainContent>
    </PageContainer>
  );
};

